"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Image from "next/image";
import { ArrowUpRight, CalendarCheck, FolderDown, Gift, Image as ImageIcon, MonitorPlay, QrCode, Smartphone, Users } from "lucide-react";
import BuyEventButton from "../buyEventButton";
import { Link } from "@/navigation";
import LoginLink from "@/components/LoginLink";
import { useLocale, useTranslations } from "next-intl";
import { useCurrencyPrices } from "@/hooks/useCurrencyPrices";
import { DetectedLocalPricingNudge } from "@/components/index/LocalPricingNudge";
import { TABLET_PROSE } from "@/components/index/sectionGutters";
import { cn } from "@/lib/utils";
import type { IncludedBookCardPromotion } from "@/lib/photobook/includedBook";
import { formatOfferPrice, type PlanPackageType, type PublicOffer } from "@/lib/offers";
interface PricingCardsProps {
  offerPlacement?: string;
  offers?: Partial<Record<PlanPackageType, PublicOffer>>;
  thankYouCardPromotion?: ThankYouCardPromotionDetails;
  /** The November photo book bundle, per package (DK only, from the offer catalog). */
  photoBookPromotion?: Partial<Record<PlanPackageType, PhotoBookPromotionDetails>>;
}

/** What a plan card says about its included photo book (includedBookCardPromotions builds it). */
export type PhotoBookPromotionDetails = IncludedBookCardPromotion;
interface ThankYouCardPromotionDetails {
  quantity: number;
  offerPlacement: string;
  badge: string;
  /** The offer's last day, e.g. "31. oktober", from the catalog's `endsAt`. */
  lastDay?: string;
}

/** Literal class lists per page theme, so Tailwind sees every colour. */
interface PricingTheme {
  basicCard: string;
  premiumCard: string;
  basicMuted: string;
  premiumMuted: string;
  basicRule: string;
  premiumRule: string;
  badge: string;
  basicButton: string;
  premiumButton: string;
  arrow: string;
  sharedIcon: string;
  sharedText: string;
  sharedBorder: string;
  footnote: string;
  /** The landing page's wedding photos; only where weddings fit the page. */
  hasPhotos: boolean;
  hasLocalPricingNudge: boolean;
}
const PREMIUM_SHADOW = "shadow-[0_30px_60px_-34px_rgba(30,30,40,0.95)]";
const BUTTON_SHADOW = "shadow-[0_14px_30px_-16px_rgba(30,30,40,0.7)]";
const PRICING_THEMES = {
  default: {
    basicCard: "bg-gg-orange-100 text-dark",
    premiumCard: `bg-dark text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-dark/75",
    premiumMuted: "text-white/75",
    basicRule: "border-dark/10",
    premiumRule: "border-white/15",
    badge: "bg-gg-orange-300 text-dark",
    basicButton: `bg-dark text-white hover:bg-[#43444f] ${BUTTON_SHADOW}`,
    premiumButton: `bg-gg-orange-100 text-dark hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-primary text-dark",
    sharedIcon: "bg-gg-orange-100 text-[#9A5A00]",
    sharedText: "text-dark",
    sharedBorder: "border-dark/10",
    footnote: "text-dark/70",
    hasPhotos: true,
    hasLocalPricingNudge: true
  },
  christmas: {
    basicCard: "bg-white text-dark ring-1 ring-dark/10",
    premiumCard: `bg-dark text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-dark/75",
    premiumMuted: "text-white/75",
    basicRule: "border-dark/10",
    premiumRule: "border-white/15",
    badge: "bg-gg-christmas-red text-white",
    basicButton: `bg-dark text-white hover:bg-[#43444f] ${BUTTON_SHADOW}`,
    premiumButton: `bg-white text-dark hover:bg-white/85 ${BUTTON_SHADOW}`,
    arrow: "bg-gg-christmas-red text-white",
    sharedIcon: "bg-[#FBEAEA] text-gg-christmas-red",
    sharedText: "text-dark",
    sharedBorder: "border-dark/10",
    footnote: "text-dark/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  konfirmation: {
    basicCard: "bg-[#F5EDE8] text-[#3A2C24]",
    premiumCard: `bg-[#3A2C24] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#3A2C24]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#3A2C24]/10",
    premiumRule: "border-white/15",
    badge: "bg-[#D9B8A8] text-[#3A2C24]",
    basicButton: `bg-[#3A2C24] text-white hover:bg-[#4A3A30] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#F5EDE8] text-[#3A2C24] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#B08E7E] text-white",
    sharedIcon: "bg-[#F5EDE8] text-[#7D5F50]",
    sharedText: "text-[#3A2C24]",
    sharedBorder: "border-[#3A2C24]/10",
    footnote: "text-[#3A2C24]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  barnedab: {
    basicCard: "bg-[#F0EBF8] text-[#2D1D42]",
    premiumCard: `bg-[#3D2858] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#2D1D42]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#2D1D42]/10",
    premiumRule: "border-white/15",
    badge: "bg-[#C9B6E4] text-[#2D1D42]",
    basicButton: `bg-[#3D2858] text-white hover:bg-[#4B3369] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#F0EBF8] text-[#2D1D42] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#8B72A8] text-white",
    sharedIcon: "bg-[#F0EBF8] text-[#5C4278]",
    sharedText: "text-[#2D1D42]",
    sharedBorder: "border-[#2D1D42]/10",
    footnote: "text-[#2D1D42]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  fodselsdag: {
    basicCard: "bg-[#FBF5E6] text-[#3A2A08]",
    premiumCard: `bg-[#3A2A08] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#3A2A08]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#3A2A08]/10",
    premiumRule: "border-white/15",
    badge: "bg-[#E5C66B] text-[#3A2A08]",
    basicButton: `bg-[#3A2A08] text-white hover:bg-[#4A370F] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#FBF5E6] text-[#3A2A08] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#8C6D1C] text-white",
    sharedIcon: "bg-[#FBF5E6] text-[#6B5016]",
    sharedText: "text-[#3A2A08]",
    sharedBorder: "border-[#3A2A08]/10",
    footnote: "text-[#3A2A08]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  barMitsva: {
    basicCard: "bg-[#E8EEF8] text-[#1A2A50]",
    premiumCard: `bg-[#1A2A50] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#1A2A50]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#1A2A50]/10",
    premiumRule: "border-white/15",
    badge: "bg-[#9DB5E6] text-[#1A2A50]",
    basicButton: `bg-[#1A2A50] text-white hover:bg-[#243766] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#E8EEF8] text-[#1A2A50] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#2C4A8C] text-white",
    sharedIcon: "bg-[#E8EEF8] text-[#2C4A8C]",
    sharedText: "text-[#1A2A50]",
    sharedBorder: "border-[#1A2A50]/10",
    footnote: "text-[#1A2A50]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  }
} as const satisfies Record<string, PricingTheme>;
type PricingThemeName = keyof typeof PRICING_THEMES;
const PHOTO_BASE_URL = "https://kycacontent.s3.eu-central-1.amazonaws.com/guestgallery/landing-page";

// Two polaroids peek over Premium's corner and fan out on hover, each with its
// own tilt, travel and timing.
const PEEK_PHOTOS = [{
  src: "3",
  className: "right-16 -rotate-[9deg] duration-[620ms] can-hover:group-hover/premium:-translate-x-3 can-hover:group-hover/premium:-translate-y-1 can-hover:group-hover/premium:-rotate-[14deg] md:right-20"
}, {
  src: "6",
  className: "right-4 rotate-[7deg] duration-500 can-hover:group-hover/premium:-translate-y-2 can-hover:group-hover/premium:rotate-[11deg] md:right-6"
}] as const;
function OfferCallout(t0) {
  const $ = _c(16);
  const {
    offer,
    isDark
  } = t0;
  if (!offer) {
    return null;
  }
  let t1;
  if ($[0] !== isDark || $[1] !== offer.badge || $[2] !== offer.benefits || $[3] !== offer.description || $[4] !== offer.headline) {
    const benefitLabels = offer.benefits.map(_temp).filter(_temp2);
    const t2 = isDark ? "bg-white/[0.08]" : "bg-white/70 ring-1 ring-black/5";
    let t3;
    if ($[6] !== t2) {
      t3 = cn("mt-5 rounded-xl px-4 py-3 text-sm", t2);
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== isDark || $[9] !== offer.badge) {
      t4 = offer.badge ? <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", isDark ? "bg-gg-orange-300 text-dark" : "bg-gg-orange-300/60 text-[#5c3d0a]")}>{offer.badge}</span> : null;
      $[8] = isDark;
      $[9] = offer.badge;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== offer.headline || $[12] !== t4) {
      t5 = <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold leading-5">{t4}{offer.headline}</p>;
      $[11] = offer.headline;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== offer.description) {
      t6 = offer.description ? <p className="mt-1 text-xs leading-5 opacity-75">{offer.description}</p> : null;
      $[14] = offer.description;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t1 = <div className={t3}>{t5}{t6}{benefitLabels.length > 0 ? <p className="mt-1 text-xs font-medium">{benefitLabels.join(" \xB7 ")}</p> : null}</div>;
    $[0] = isDark;
    $[1] = offer.badge;
    $[2] = offer.benefits;
    $[3] = offer.description;
    $[4] = offer.headline;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp2(label) {
  return Boolean(label);
}
function _temp(benefit) {
  return benefit.label;
}
function ThankYouCardsPromotion(t0) {
  const $ = _c(16);
  const {
    promotion,
    className
  } = t0;
  const locale = useLocale();
  if (locale !== "da" || !promotion) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("mb-1 flex gap-3 rounded-xl bg-[#fff8e5] p-3.5 text-sm text-[#29251f]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#f3dfae] text-[#a64e39]"><Gift className="h-4 w-4" /></span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="whitespace-nowrap font-normal text-[#655d52]">(Værdi 240 kr.)</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== promotion.quantity) {
    t4 = <p className="font-semibold leading-5">{promotion.quantity} gratis takkekort inkluderet{" "}{t3}</p>;
    $[4] = promotion.quantity;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = promotion.lastDay ? ` til og med ${promotion.lastDay}` : null;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Link href="/printed-for-you" className="inline-flex items-center gap-0.5 font-semibold text-[#2f4f49] underline decoration-[#2f4f49]/30 underline-offset-4 hover:text-[#a64e39]">Læs mere<ArrowUpRight aria-hidden="true" className="h-3 w-3" /></Link>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== promotion.badge || $[8] !== t5) {
    t7 = <p className="mt-0.5 text-xs leading-5 text-[#655d52]">{promotion.badge}{t5} · Med et billede efter eget valg og en QR-kode direkte til jeres galleri. Fragt 49 kr. · Ekstra takkekort: 12 kr. pr. stk.{" "}{t6}</p>;
    $[7] = promotion.badge;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t4}{t7}</div>;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t1 || $[14] !== t8) {
    t9 = <aside className={t1}>{t2}{t8}</aside>;
    $[13] = t1;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}

/**
 * The anchor the November photo book ads link to (`/da#fotobog`): on Basic's
 * box only, the first in reading order, so the id is unique on the page.
 * Outside the window there's no box and no anchor; the page loads at the top.
 */
export const PHOTO_BOOK_ANCHOR_ID = "fotobog";
function PhotoBookPromotion(t0) {
  const $ = _c(22);
  const {
    promotion,
    isPremium
  } = t0;
  const locale = useLocale();
  if (locale !== "da" || !promotion) {
    return null;
  }
  const t1 = isPremium ? undefined : PHOTO_BOOK_ANCHOR_ID;
  const t2 = isPremium ? "bg-[#fff8e5]" : "bg-white ring-1 ring-dark/10";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("mb-3 scroll-mt-28 rounded-xl p-3.5 text-sm text-[#29251f] tablet-short:mb-0", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== promotion.headline) {
    t4 = <p className="font-semibold leading-5">{promotion.headline}</p>;
    $[2] = promotion.headline;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = isPremium ? "bg-gg-orange-300 text-dark" : "bg-dark/5 text-[#4a443c]";
  let t6;
  if ($[4] !== t5) {
    t6 = cn("mr-1.5 inline-block rounded-full px-2 text-xs font-medium leading-5", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== promotion.value || $[7] !== t6) {
    t7 = <span className={t6}>{promotion.value}</span>;
    $[6] = promotion.value;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== promotion.spec || $[10] !== t7) {
    t8 = <p className="mt-1 leading-5 text-[#4a443c]">{t7}{promotion.spec}</p>;
    $[9] = promotion.spec;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== promotion.conditions) {
    t9 = promotion.conditions.split(" \xB7 ").map(_temp3);
    $[12] = promotion.conditions;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <p className="mt-1 leading-5 text-[#6b6358]">{t9}</p>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t1 || $[17] !== t10 || $[18] !== t3 || $[19] !== t4 || $[20] !== t8) {
    t11 = <aside id={t1} className={t3}>{t4}{t8}{t10}</aside>;
    $[16] = t1;
    $[17] = t10;
    $[18] = t3;
    $[19] = t4;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}
function _temp3(condition, index, conditions) {
  return <React.Fragment key={condition}><span className="whitespace-nowrap">{condition}{index < conditions.length - 1 ? " \xB7" : null}</span>{index < conditions.length - 1 ? " " : null}</React.Fragment>;
}
function PlanCard(t0) {
  const $ = _c(152);
  const {
    packageType,
    theme,
    offer,
    offerPlacement,
    thankYouCardPromotion,
    photoBookPromotion
  } = t0;
  const isPremium = packageType === "premium";
  const t = useTranslations(isPremium ? "Pricing.Premium" : "Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] !== currencyPrices?.basicPrice || $[4] !== currencyPrices?.premiumPrice || $[5] !== isPremium || $[6] !== offerPrice || $[7] !== t) {
    t2 = offerPrice ?? (isPremium ? currencyPrices?.premiumPrice : currencyPrices?.basicPrice) ?? t("price");
    $[3] = currencyPrices?.basicPrice;
    $[4] = currencyPrices?.premiumPrice;
    $[5] = isPremium;
    $[6] = offerPrice;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const price = t2;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== currencyPrices?.rebateAmount || $[10] !== isPremium || $[11] !== locale || $[12] !== offer || $[13] !== offerPlacement || $[14] !== offerPrice || $[15] !== packageType || $[16] !== photoBookPromotion || $[17] !== price || $[18] !== t || $[19] !== tPricing || $[20] !== thankYouCardPromotion || $[21] !== theme.arrow || $[22] !== theme.badge || $[23] !== theme.basicButton || $[24] !== theme.basicCard || $[25] !== theme.basicMuted || $[26] !== theme.basicRule || $[27] !== theme.hasPhotos || $[28] !== theme.premiumButton || $[29] !== theme.premiumCard || $[30] !== theme.premiumMuted || $[31] !== theme.premiumRule) {
    const name = isPremium ? t("PricingCard.title") : "Basic";
    const muted = isPremium ? theme.premiumMuted : theme.basicMuted;
    let t12;
    if ($[41] !== currencyPrices?.rebateAmount || $[42] !== isPremium || $[43] !== offerPrice || $[44] !== t || $[45] !== tPricing) {
      t12 = isPremium && !offerPrice ? tPricing("savingVsUpgrade", {
        amount: currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount")
      }) : null;
      $[41] = currencyPrices?.rebateAmount;
      $[42] = isPremium;
      $[43] = offerPrice;
      $[44] = t;
      $[45] = tPricing;
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    const footnote = t12;
    const hasPromotion = locale === "da" && (photoBookPromotion !== undefined || isPremium && thankYouCardPromotion !== undefined);
    const renderCheckout = hiddenClassName => <><BuyEventButton appearance="pill" variant="dark" className={cn(isPremium ? theme.premiumButton : theme.basicButton, hiddenClassName)} arrowClassName={theme.arrow} label={tPricing("choosePlan", {
        plan: name
      })} packageType={packageType} offerPlacement={isPremium ? thankYouCardPromotion?.offerPlacement ?? offerPlacement : offerPlacement} offer={offer} /><p className={cn("text-center text-xs md:min-h-4", !footnote && "hidden md:block", muted, hiddenClassName)}>{footnote}</p></>;
    let t13;
    if ($[47] !== isPremium || $[48] !== theme.basicCard || $[49] !== theme.premiumCard) {
      t13 = isPremium ? cn("group/premium", theme.premiumCard) : theme.basicCard;
      $[47] = isPremium;
      $[48] = theme.basicCard;
      $[49] = theme.premiumCard;
      $[50] = t13;
    } else {
      t13 = $[50];
    }
    if ($[51] !== t13) {
      t6 = cn("relative flex flex-col rounded-[1.75rem] p-6 lg:p-8", t13);
      $[51] = t13;
      $[52] = t6;
    } else {
      t6 = $[52];
    }
    if ($[53] !== isPremium || $[54] !== theme.hasPhotos) {
      t7 = isPremium && theme.hasPhotos ? PEEK_PHOTOS.map(_temp4) : null;
      $[53] = isPremium;
      $[54] = theme.hasPhotos;
      $[55] = t7;
    } else {
      t7 = $[55];
    }
    let t14;
    if ($[56] !== isPremium || $[57] !== tPricing || $[58] !== theme.badge) {
      t14 = isPremium ? <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", theme.badge)}>{tPricing("recommended")}</span> : null;
      $[56] = isPremium;
      $[57] = tPricing;
      $[58] = theme.badge;
      $[59] = t14;
    } else {
      t14 = $[59];
    }
    const t15 = <div className="flex items-center gap-2"><h3 className="text-lg font-semibold">{name}</h3>{t14}</div>;
    let t16;
    if ($[60] !== price) {
      t16 = <span className="text-5xl font-bold tracking-[-0.03em] tabular-nums">{price}</span>;
      $[60] = price;
      $[61] = t16;
    } else {
      t16 = $[61];
    }
    let t17;
    if ($[62] !== t) {
      t17 = t("PricingCard.pricePostfix");
      $[62] = t;
      $[63] = t17;
    } else {
      t17 = $[63];
    }
    let t18;
    if ($[64] !== muted || $[65] !== t17) {
      t18 = <span className={muted}>{t17}</span>;
      $[64] = muted;
      $[65] = t17;
      $[66] = t18;
    } else {
      t18 = $[66];
    }
    let t19;
    if ($[67] !== t16 || $[68] !== t18) {
      t19 = <p className="mt-2 flex flex-wrap items-baseline gap-x-2">{t16}{t18}</p>;
      $[67] = t16;
      $[68] = t18;
      $[69] = t19;
    } else {
      t19 = $[69];
    }
    let t20;
    if ($[70] !== muted) {
      t20 = cn("text-sm", muted);
      $[70] = muted;
      $[71] = t20;
    } else {
      t20 = $[71];
    }
    let t21;
    if ($[72] !== tPricing) {
      t21 = tPricing("oneTimePayment");
      $[72] = tPricing;
      $[73] = t21;
    } else {
      t21 = $[73];
    }
    let t22;
    if ($[74] !== t20 || $[75] !== t21) {
      t22 = <p className={t20}>{t21}</p>;
      $[74] = t20;
      $[75] = t21;
      $[76] = t22;
    } else {
      t22 = $[76];
    }
    if ($[77] !== t15 || $[78] !== t19 || $[79] !== t22) {
      t8 = <header className="flex flex-col gap-1">{t15}{t19}{t22}</header>;
      $[77] = t15;
      $[78] = t19;
      $[79] = t22;
      $[80] = t8;
    } else {
      t8 = $[80];
    }
    t9 = <div className="hidden tablet-short:mt-5 tablet-short:flex tablet-short:flex-col tablet-short:gap-2">{renderCheckout()}</div>;
    if ($[81] !== isPremium || $[82] !== offer) {
      t10 = <OfferCallout offer={offer} isDark={isPremium} />;
      $[81] = isPremium;
      $[82] = offer;
      $[83] = t10;
    } else {
      t10 = $[83];
    }
    const t23 = isPremium ? theme.premiumRule : theme.basicRule;
    let t24;
    if ($[84] !== t23) {
      t24 = cn("mt-6 flex flex-col gap-4 border-t pt-6 tablet-short:mt-4 tablet-short:pt-4", t23);
      $[84] = t23;
      $[85] = t24;
    } else {
      t24 = $[85];
    }
    let t25;
    if ($[86] !== muted) {
      t25 = cn("text-sm", muted);
      $[86] = muted;
      $[87] = t25;
    } else {
      t25 = $[87];
    }
    let t26;
    if ($[88] !== tPricing) {
      t26 = tPricing("uploadsLabel");
      $[88] = tPricing;
      $[89] = t26;
    } else {
      t26 = $[89];
    }
    let t27;
    if ($[90] !== t25 || $[91] !== t26) {
      t27 = <dt className={t25}>{t26}</dt>;
      $[90] = t25;
      $[91] = t26;
      $[92] = t27;
    } else {
      t27 = $[92];
    }
    let t28;
    if ($[93] !== t) {
      t28 = t("storageShort");
      $[93] = t;
      $[94] = t28;
    } else {
      t28 = $[94];
    }
    let t29;
    if ($[95] !== t28) {
      t29 = <dd className="text-2xl font-bold tracking-[-0.02em] md:max-lg:text-xl">{t28}</dd>;
      $[95] = t28;
      $[96] = t29;
    } else {
      t29 = $[96];
    }
    let t30;
    if ($[97] !== t27 || $[98] !== t29) {
      t30 = <div className="flex flex-col justify-between gap-1">{t27}{t29}</div>;
      $[97] = t27;
      $[98] = t29;
      $[99] = t30;
    } else {
      t30 = $[99];
    }
    let t31;
    if ($[100] !== muted) {
      t31 = cn("text-sm", muted);
      $[100] = muted;
      $[101] = t31;
    } else {
      t31 = $[101];
    }
    let t32;
    if ($[102] !== tPricing) {
      t32 = tPricing("galleryOpenLabel");
      $[102] = tPricing;
      $[103] = t32;
    } else {
      t32 = $[103];
    }
    let t33;
    if ($[104] !== t31 || $[105] !== t32) {
      t33 = <dt className={t31}>{t32}</dt>;
      $[104] = t31;
      $[105] = t32;
      $[106] = t33;
    } else {
      t33 = $[106];
    }
    let t34;
    if ($[107] !== t) {
      t34 = t("windowShort");
      $[107] = t;
      $[108] = t34;
    } else {
      t34 = $[108];
    }
    let t35;
    if ($[109] !== t34) {
      t35 = <dd className="text-2xl font-bold tracking-[-0.02em] md:max-lg:text-xl">{t34}</dd>;
      $[109] = t34;
      $[110] = t35;
    } else {
      t35 = $[110];
    }
    let t36;
    if ($[111] !== t33 || $[112] !== t35) {
      t36 = <div className="flex flex-col justify-between gap-1">{t33}{t35}</div>;
      $[111] = t33;
      $[112] = t35;
      $[113] = t36;
    } else {
      t36 = $[113];
    }
    let t37;
    if ($[114] !== t30 || $[115] !== t36) {
      t37 = <dl className="grid grid-cols-2 gap-4 max-[411px]:[&_dd]:text-xl max-[359px]:[&_dd]:text-base md:max-[809px]:[&_dd]:text-xl">{t30}{t36}</dl>;
      $[114] = t30;
      $[115] = t36;
      $[116] = t37;
    } else {
      t37 = $[116];
    }
    let t38;
    if ($[117] !== t) {
      t38 = t("PricingCard.EventBenefits.upload");
      $[117] = t;
      $[118] = t38;
    } else {
      t38 = $[118];
    }
    let t39;
    if ($[119] !== t38) {
      t39 = <p className="text-sm leading-5">{t38}</p>;
      $[119] = t38;
      $[120] = t39;
    } else {
      t39 = $[120];
    }
    let t40;
    if ($[121] !== muted) {
      t40 = cn("text-sm leading-5", muted);
      $[121] = muted;
      $[122] = t40;
    } else {
      t40 = $[122];
    }
    let t41;
    if ($[123] !== t) {
      t41 = t("PricingCard.EventBenefits.imagesAndVideos");
      $[123] = t;
      $[124] = t41;
    } else {
      t41 = $[124];
    }
    let t42;
    if ($[125] !== t40 || $[126] !== t41) {
      t42 = <p className={t40}>{t41}</p>;
      $[125] = t40;
      $[126] = t41;
      $[127] = t42;
    } else {
      t42 = $[127];
    }
    if ($[128] !== t24 || $[129] !== t37 || $[130] !== t39 || $[131] !== t42) {
      t11 = <div className={t24}>{t37}{t39}{t42}</div>;
      $[128] = t24;
      $[129] = t37;
      $[130] = t39;
      $[131] = t42;
      $[132] = t11;
    } else {
      t11 = $[132];
    }
    const t43 = photoBookPromotion ? "pt-6" : "pt-7";
    const t44 = hasPromotion ? "tablet-short:mt-4 tablet-short:pt-0" : "tablet-short:hidden";
    if ($[133] !== t43 || $[134] !== t44) {
      t3 = cn("mt-auto flex flex-col gap-3", t43, t44);
      $[133] = t43;
      $[134] = t44;
      $[135] = t3;
    } else {
      t3 = $[135];
    }
    if ($[136] !== isPremium || $[137] !== photoBookPromotion || $[138] !== thankYouCardPromotion) {
      t4 = photoBookPromotion ? <PhotoBookPromotion promotion={photoBookPromotion} isPremium={isPremium} /> : isPremium ? <ThankYouCardsPromotion promotion={thankYouCardPromotion} className="tablet-short:mb-0" /> : null;
      $[136] = isPremium;
      $[137] = photoBookPromotion;
      $[138] = thankYouCardPromotion;
      $[139] = t4;
    } else {
      t4 = $[139];
    }
    t5 = renderCheckout("tablet-short:!hidden");
    $[9] = currencyPrices?.rebateAmount;
    $[10] = isPremium;
    $[11] = locale;
    $[12] = offer;
    $[13] = offerPlacement;
    $[14] = offerPrice;
    $[15] = packageType;
    $[16] = photoBookPromotion;
    $[17] = price;
    $[18] = t;
    $[19] = tPricing;
    $[20] = thankYouCardPromotion;
    $[21] = theme.arrow;
    $[22] = theme.badge;
    $[23] = theme.basicButton;
    $[24] = theme.basicCard;
    $[25] = theme.basicMuted;
    $[26] = theme.basicRule;
    $[27] = theme.hasPhotos;
    $[28] = theme.premiumButton;
    $[29] = theme.premiumCard;
    $[30] = theme.premiumMuted;
    $[31] = theme.premiumRule;
    $[32] = t10;
    $[33] = t11;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    t10 = $[32];
    t11 = $[33];
    t3 = $[34];
    t4 = $[35];
    t5 = $[36];
    t6 = $[37];
    t7 = $[38];
    t8 = $[39];
    t9 = $[40];
  }
  let t12;
  if ($[140] !== t3 || $[141] !== t4 || $[142] !== t5) {
    t12 = <div className={t3}>{t4}{t5}</div>;
    $[140] = t3;
    $[141] = t4;
    $[142] = t5;
    $[143] = t12;
  } else {
    t12 = $[143];
  }
  let t13;
  if ($[144] !== t10 || $[145] !== t11 || $[146] !== t12 || $[147] !== t6 || $[148] !== t7 || $[149] !== t8 || $[150] !== t9) {
    t13 = <article className={t6}>{t7}{t8}{t9}{t10}{t11}{t12}</article>;
    $[144] = t10;
    $[145] = t11;
    $[146] = t12;
    $[147] = t6;
    $[148] = t7;
    $[149] = t8;
    $[150] = t9;
    $[151] = t13;
  } else {
    t13 = $[151];
  }
  return t13;
}
function _temp4(photo) {
  return <div key={photo.src} aria-hidden="true" className={cn("absolute -top-9 w-14 rounded-[4px] bg-white p-1 pb-3.5 shadow-[0_12px_24px_-12px_rgba(0,0,0,0.7)] transition-transform ease-[var(--ease-overshoot)] motion-reduce:transition-none md:-top-11 md:w-[4.5rem]", photo.className)}><div className="relative aspect-[4/5] overflow-hidden rounded-[2px] bg-stone-200"><Image alt="" className="object-cover" fill={true} sizes="72px" src={`${PHOTO_BASE_URL}/${photo.src}.webp`} /></div></div>;
}
const SHARED_FEATURES = [{
  icon: ImageIcon,
  key: "Basic.PricingCard.EventBenefits.originalQuality"
}, {
  icon: Users,
  key: "Basic.PricingCard.EventBenefits.downloadable"
}, {
  icon: Smartphone,
  key: "Included.noApp"
}, {
  icon: FolderDown,
  key: "Included.zip"
}, {
  icon: MonitorPlay,
  key: "Included.photowall"
}, {
  icon: QrCode,
  key: "Included.qrCards"
}, {
  icon: CalendarCheck,
  key: "chooseDateNote"
}] as const;

/**
 * Basic and Premium side by side: price, the two facts that differ (storage
 * and months open), then everything both plans share, listed once.
 */
function PricingPlans(t0) {
  const $ = _c(68);
  const {
    themeName,
    offerPlacement,
    offers,
    thankYouCardPromotion,
    photoBookPromotion
  } = t0;
  const t = useTranslations("Pricing");
  const theme = PRICING_THEMES[themeName];
  let t1;
  if ($[0] !== theme.hasLocalPricingNudge) {
    t1 = theme.hasLocalPricingNudge ? <DetectedLocalPricingNudge className="mb-1 self-center" /> : null;
    $[0] = theme.hasLocalPricingNudge;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = theme.hasPhotos ? "gap-y-14 md:gap-y-4 md:pt-10" : "gap-y-4";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("grid grid-cols-1 gap-x-4 min-[600px]:max-md:mx-auto min-[600px]:max-md:w-full min-[600px]:max-md:max-w-md md:grid-cols-2 tablet-short:items-start", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = offers?.basic;
  const t5 = photoBookPromotion?.basic;
  let t6;
  if ($[4] !== offerPlacement || $[5] !== t4 || $[6] !== t5 || $[7] !== theme) {
    t6 = <PlanCard packageType="basic" theme={theme} offer={t4} offerPlacement={offerPlacement} photoBookPromotion={t5} />;
    $[4] = offerPlacement;
    $[5] = t4;
    $[6] = t5;
    $[7] = theme;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = offers?.premium;
  const t8 = photoBookPromotion?.premium;
  let t9;
  if ($[9] !== offerPlacement || $[10] !== t7 || $[11] !== t8 || $[12] !== thankYouCardPromotion || $[13] !== theme) {
    t9 = <PlanCard packageType="premium" theme={theme} offer={t7} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} photoBookPromotion={t8} />;
    $[9] = offerPlacement;
    $[10] = t7;
    $[11] = t8;
    $[12] = thankYouCardPromotion;
    $[13] = theme;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t3 || $[16] !== t6 || $[17] !== t9) {
    t10 = <div className={t3}>{t6}{t9}</div>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== theme.footnote) {
    t11 = cn("text-center text-xs", theme.footnote);
    $[19] = theme.footnote;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("secureCheckout");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t12) {
    t13 = <p className={t11}>{t12}</p>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== theme.sharedBorder || $[27] !== theme.sharedText) {
    t14 = cn("rounded-[1.75rem] border px-6 py-6 md:px-8", theme.sharedBorder, theme.sharedText);
    $[26] = theme.sharedBorder;
    $[27] = theme.sharedText;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t) {
    t15 = t("bothPlansInclude");
    $[29] = t;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <h3 className="text-base font-semibold">{t15}</h3>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t || $[34] !== theme.sharedIcon) {
    t17 = SHARED_FEATURES.map(t18 => {
      const {
        icon: Icon,
        key
      } = t18;
      return <li key={key} className="flex items-start gap-2.5"><span aria-hidden="true" className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-lg", theme.sharedIcon)}><Icon className="h-3.5 w-3.5" /></span><span className="pt-1 leading-5">{t(key)}</span></li>;
    });
    $[33] = t;
    $[34] = theme.sharedIcon;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17) {
    t18 = <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">{t17}</ul>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t14 || $[39] !== t16 || $[40] !== t18) {
    t19 = <section className={t14}>{t16}{t18}</section>;
    $[38] = t14;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== theme.footnote) {
    t20 = cn("text-sm leading-5", TABLET_PROSE, theme.footnote);
    $[42] = theme.footnote;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t) {
    t21 = t.rich("trialNote", {
      trial: _temp5
    });
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20 || $[47] !== t21) {
    t22 = <p className={t20}>{t21}</p>;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== theme.footnote) {
    t23 = cn("text-xs leading-5", theme.footnote);
    $[49] = theme.footnote;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t("fairUsePolicy");
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t) {
    t25 = t("fairUseLink");
    $[53] = t;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t25) {
    t26 = <Link href="/terms-and-conditions" className="underline underline-offset-2" target="_blank">{t25}</Link>;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t23 || $[58] !== t24 || $[59] !== t26) {
    t27 = <p className={t23}>*{t24}{" "}{t26}.</p>;
    $[57] = t23;
    $[58] = t24;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t1 || $[62] !== t10 || $[63] !== t13 || $[64] !== t19 || $[65] !== t22 || $[66] !== t27) {
    t28 = <div className="flex flex-col gap-4">{t1}{t10}{t13}{t19}{t22}{t27}</div>;
    $[61] = t1;
    $[62] = t10;
    $[63] = t13;
    $[64] = t19;
    $[65] = t22;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  return t28;
}
function _temp5(chunks) {
  return <LoginLink className="underline underline-offset-2">{chunks}</LoginLink>;
}
export function PricingCards(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="default" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsChristmas(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="christmas" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsKonfirmation(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="konfirmation" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsBarnedab(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="barnedab" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsFodselsdag(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="fodselsdag" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsBarMitsva(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="barMitsva" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}